// Paketler sayfası boş durumları (DR6). Paket yokken katalog durumuna göre üç dal: kategori yok →
// rol adıyla açıklama (birincil eylem yok — kategori Sistem Yöneticisi'nde); kategori var gösterge
// yok → birincil "Excel'den kataloğa aktar"; katalog hazır → birincil "+ İlk paketi oluştur".
// Ek durumlar: seçili pakette gösterge yok → "Katalogdan seç" + "✦ AI ile öner"; arama boş → "Eşleşen
// paket yok". Seçim saf `packageEmptyState` (test edilir); bileşen yalnız görünüm.

import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { decisionText, footerBtn, helpText } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

export type PackageEmptyKind =
  | "noCategories"
  | "noCatalogItems"
  | "noPackages"
  | "noMembers"
  | "noMatch";

/** Hangi boş durum (null → boş durum yok). Katalog sayıları yüklenmediyse `null` gelir. */
export function packageEmptyState(a: {
  packageCount: number;
  /** arama/süzgeç sonrası görünen paket sayısı */
  visibleCount: number;
  catalog: { categories: number; items: number } | null;
  /** seçili paketin taslaktaki üye sayısı (seçim yoksa null) */
  selectedMembers: number | null;
}): PackageEmptyKind | null {
  if (a.packageCount === 0) {
    if (a.catalog?.categories === 0) return "noCategories";
    if (a.catalog?.items === 0) return "noCatalogItems";
    return "noPackages";
  }
  if (a.visibleCount === 0) return "noMatch";
  if (a.selectedMembers === 0) return "noMembers";
  return null;
}

export interface PackageEmptyStatesProps {
  kind: PackageEmptyKind;
  canManage: boolean;
  onCreate?: () => void;
  onAddFromCatalog?: () => void;
  /** boş paket: "✦ AI ile öner" */
  onSuggest?: () => void;
}

const linkBtn = (primary: boolean): CSSProperties => ({
  ...footerBtn(primary),
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
});

export function PackageEmptyStates(p: PackageEmptyStatesProps): JSX.Element {
  const box: CSSProperties = {
    border: "1px dashed var(--color-border)",
    borderRadius: 12,
    padding: 24,
    maxWidth: 760,
    display: "grid",
    gap: 12,
  };
  if (p.kind === "noMatch") {
    return (
      <p
        role="status"
        style={{ ...helpText, margin: "12px 0" }}
        data-testid="package-empty-noMatch"
      >
        {C.empty.noMatch}
      </p>
    );
  }
  if (p.kind === "noMembers") {
    return (
      <div
        style={{ ...box, margin: "0 auto", textAlign: "center", justifyItems: "center" }}
        data-testid="package-empty-noMembers"
      >
        <p style={{ ...decisionText, margin: 0 }}>{C.empty.noMembers}</p>
        {p.canManage && (p.onAddFromCatalog || p.onSuggest) ? (
          <div className="flex flex-wrap items-center gap-2">
            {p.onAddFromCatalog ? (
              <button type="button" style={footerBtn(true)} onClick={p.onAddFromCatalog}>
                {C.empty.fromCatalog}
              </button>
            ) : null}
            {p.onSuggest ? (
              <button type="button" style={footerBtn()} onClick={p.onSuggest}>
                {C.header.ai}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div style={box} data-testid={`package-empty-${p.kind}`}>
      <p style={{ ...decisionText, margin: 0, fontWeight: 600 }}>{C.empty.noPackages}</p>
      <p style={{ ...helpText, margin: 0 }}>{C.page.description}</p>
      {p.kind === "noCategories" ? (
        <p style={{ ...helpText, margin: 0 }}>{C.empty.noCategories}</p>
      ) : null}
      {p.kind === "noCatalogItems" ? (
        <p style={{ ...helpText, margin: 0 }}>{C.empty.noItemsInCatalog}</p>
      ) : null}
      {p.canManage && p.kind !== "noCategories" ? (
        <div className="flex flex-wrap items-center gap-2">
          {p.kind === "noCatalogItems" ? (
            <Link to="/clients/kpi/katalog" style={linkBtn(true)}>
              {C.empty.importFromExcel}
            </Link>
          ) : (
            <>
              {p.onCreate ? (
                <button type="button" style={footerBtn(true)} onClick={p.onCreate}>
                  {C.empty.firstPackage}
                </button>
              ) : null}
              <Link to="/clients/kpi/katalog" style={linkBtn(false)}>
                {C.empty.importFromExcel}
              </Link>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
